import React, { useContext, useMemo, useState } from 'react';

import { Checkbox, ListItemText, MenuItem, Grid, Skeleton, Table, TableRow, TableCell, TableBody } from '@mui/material';
import { isArray } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassSessionsStudentToolbar from './ClassSessionsStudentToolbar';
import ApiError from './ContentTypes/ApiError';
import ClassEvent from './ContentTypes/ClassEvent';
import Generic from './ContentTypes/Generic';
import Tab from './ContentTypes/Tab';
import { StyledRoleSelect } from '../../../../../styles/input-row';
import AdminAuthContainer from '../../../AdminAuthContainer';
import { ClassSessionContext } from '../../../ClassSessionContext';

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

export const ClassSessionDetailContent = () => {
    const { t } = useTranslation('portal');
    const { classSessionContent } = useContext(ClassSessionContext);
    const { authMode } = useContainer(AdminAuthContainer);
    console.log('ClassSessionDetailContent classSessionContent', classSessionContent, 'authMode', authMode);

    const [searchText, setSearchText] = useState('');
    const defaultActionFilter = ['chat', 'note', 'transcript', 'ai', 'tab'];

    if (authMode === 'customerSupport') {
        defaultActionFilter.push('event');
        defaultActionFilter.push('error');
    }
    const [actionFilter, setActionFilter] = useState<string[]>(defaultActionFilter);
    console.log('actionFilter', actionFilter);

    const filteredContent = useMemo(
        () =>
            classSessionContent.items.filter(
                (content) =>
                    actionFilter.includes(content.type) &&
                    content.text_content.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
            ),
        [actionFilter, searchText, classSessionContent]
    );
    console.log('filteredContent', filteredContent);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const handleActionFilter = (action: string[]) => {
        setActionFilter(action);
    };
    const handleActionFilterChange = (event) => {
        console.log('handleActionFilterChange', event);
        handleActionFilter(event.target.value);
    };

    const filterOptions = [
        {
            label: 'Tabs',
            value: 'tab'
        },
        {
            label: 'Chats',
            value: 'chat'
        },
        {
            label: 'Notes',
            value: 'note'
        },
        {
            label: 'AI',
            value: 'ai'
        },
        {
            label: 'Transcripts',
            value: 'transcript'
        }
    ];

    if (authMode === 'customerSupport') {
        filterOptions.push({
            label: 'Events',
            value: 'event'
        });
        filterOptions.push({
            label: 'Errors',
            value: 'error'
        });
        filterOptions.push({
            label: 'Updates',
            value: 'class_session_state_update'
        });
    }

    if (classSessionContent.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <>
            <Table style={{ width: '100%', padding: '0px', margin: '0px' }}>
                <TableBody style={{ width: '100%', padding: '0px', margin: '0px' }}>
                    <TableRow style={{ width: '100%', padding: '0px', margin: '0px' }}>
                        <TableCell>
                            <ClassSessionsStudentToolbar
                                searchText={searchText}
                                handleChangeSearch={handleChangeSearch}
                            />
                        </TableCell>

                        <TableCell>
                            <StyledRoleSelect
                                name="roles"
                                id="demo-multiple-checkbox"
                                multiple
                                value={actionFilter}
                                onChange={(event) => handleActionFilterChange(event)}
                                renderValue={(selected) =>
                                    `${isArray(selected) ? selected.length : '0'} ${t('event-types')}`
                                }
                            >
                                {filterOptions.map((filterOption) => (
                                    <MenuItem key={filterOption.value} value={filterOption.value}>
                                        <Checkbox checked={actionFilter.indexOf(filterOption.value) > -1} />
                                        <ListItemText primary={t(filterOption.label)} />
                                    </MenuItem>
                                ))}
                            </StyledRoleSelect>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <Spacer />
            <Spacer />

            {filteredContent.map((item, i) => {
                switch (item.type) {
                    case 'tab':
                        return <Tab key={i} item={item} />;
                    case 'error':
                        return <ApiError format="json" key={i} item={item} />;
                    case 'event':
                        return <ClassEvent format="json" key={i} item={item} />;
                    case 'class_session_state_update':
                        return <Generic format="json" key={i} item={item} />;
                    case 'chat':
                    case 'note':
                    case 'transcript':
                    case 'ai':
                    default:
                        return <></>;
                }
            })}
        </>
    );
};
